import axios from 'axios';

export interface EtymologyData {
  word: string;
  protoRoot: string;
  originLanguage: string;
  originalMeaning: string;
  modernDefinition: string;
  modernUsage: string;
  frequencyData?: {
    ancient: number;
    modern: number;
  };
}

/**
 * Fetch etymology from Wiktionary API
 */
async function fetchWiktionaryEtymology(word: string): Promise<{
  etymology: string;
  definition: string;
}> {
  try {
    const response = await axios.get(
      `https://en.wiktionary.org/api/rest_v1/page/definition/${encodeURIComponent(word.toLowerCase())}`
    );

    const etymologyText = response.data?.en?.[0]?.text || '';
    const definitions = response.data?.en?.[1]?.text || '';

    return {
      etymology: etymologyText,
      definition: definitions,
    };
  } catch (error) {
    console.error('Wiktionary fetch failed:', error);
    return { etymology: '', definition: '' };
  }
}

/**
 * Fetch modern definition from DictionaryAPI
 */
async function fetchDictionaryDefinition(word: string): Promise<{
  definition: string;
  partOfSpeech: string;
  example: string;
}> {
  try {
    const response = await axios.get(
      `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word.toLowerCase())}`
    );

    if (!response.data || response.data.length === 0) {
      return { definition: 'Definition not found', partOfSpeech: '', example: '' };
    }

    const entry = response.data[0];
    const meaning = entry.meanings?.[0];
    const definition = meaning?.definitions?.[0]?.definition || 'No definition found';
    const example = meaning?.definitions?.[0]?.example || '';

    return {
      definition,
      partOfSpeech: meaning?.partOfSpeech || '',
      example,
    };
  } catch (error) {
    console.error('DictionaryAPI fetch failed:', error);
    return { definition: 'Definition not found', partOfSpeech: '', example: '' };
  }
}

/**
 * Parse etymology text to extract proto-root information
 * This is a heuristic approach—real data will vary
 */
function parseEtymologyText(
  etymologyText: string,
  word: string
): { protoRoot: string; originLanguage: string; meaning: string } {
  // Try to identify common etymology patterns
  const patterns = [
    /(?:from|derived from|comes from)\s+(?:the\s+)?([A-Za-z\s-]+?)\s+([A-Za-z\s-]+?)(?:meaning|meaning of|meaning:\s+)?(.+?)(?:\.|,|$)/i,
    /([A-Za-z-]+)\s+\(([A-Za-z\s]+)\)\s+meaning\s+(.+?)(?:\.|,|$)/i,
  ];

  for (const pattern of patterns) {
    const match = etymologyText.match(pattern);
    if (match) {
      return {
        protoRoot: match[1]?.trim() || word,
        originLanguage: match[2]?.trim() || 'Proto-Indo-European',
        meaning: match[3]?.trim() || 'To speak',
      };
    }
  }

  // Fallback heuristics
  if (etymologyText.toLowerCase().includes('sanskrit')) {
    return {
      protoRoot: word,
      originLanguage: 'Sanskrit',
      meaning: 'Ancient root meaning (from Sanskrit traditions)',
    };
  }

  if (etymologyText.toLowerCase().includes('hebrew')) {
    return {
      protoRoot: word,
      originLanguage: 'Hebrew',
      meaning: 'Ancient root meaning (from Hebrew traditions)',
    };
  }

  return {
    protoRoot: word,
    originLanguage: 'Proto-Indo-European',
    meaning: 'To speak / To breathe',
  };
}

/**
 * Main function to fetch all etymology and definition data
 */
export async function fetchCompleteEtymology(
  word: string
): Promise<EtymologyData> {
  const [wiktionaryData, dictionaryData] = await Promise.all([
    fetchWiktionaryEtymology(word),
    fetchDictionaryDefinition(word),
  ]);

  const etymologyParsed = parseEtymologyText(wiktionaryData.etymology, word);

  return {
    word,
    protoRoot: etymologyParsed.protoRoot,
    originLanguage: etymologyParsed.originLanguage,
    originalMeaning: etymologyParsed.meaning,
    modernDefinition: dictionaryData.definition,
    modernUsage: dictionaryData.example || `Usage: in modern context and digital landscape`,
  };
}
